import { useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatMoney, getSellingPlans } from "@/lib/shopify";
import { productByHandleQueryOptions } from "@/lib/shopify-queries";
import { useCartStore } from "@/stores/cartStore";
import { CURRENT_CHAPTER_FRAGRANCES } from "@/lib/chapter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROMAN = ["I", "II", "III", "IV", "V"];

/** Number of vials each variant title implies. */
function vialCountFromTitle(title: string): number | null {
  const m = title.match(/(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return [1, 3, 5].includes(n) ? n : null;
}

function shortName(full: string): string {
  // "The Archivist" -> "Archivist" for the summary chip
  return full.replace(/^(The|A)\s+/i, "");
}

export function ChapterVialsBuyBox({
  slug,
  fallbackPrice,
}: {
  slug: string;
  fallbackPrice: string;
}) {
  const { data: product } = useSuspenseQuery(productByHandleQueryOptions(slug));

  const variants = useMemo(
    () =>
      (product?.variants.edges.map((e) => e.node) ?? [])
        .map((v) => ({ ...v, vialCount: vialCountFromTitle(v.title) }))
        .filter((v) => v.vialCount !== null)
        .sort((a, b) => (a.vialCount ?? 0) - (b.vialCount ?? 0)),
    [product],
  );

  const [variantId, setVariantId] = useState<string | undefined>();
  const selected = useMemo(
    () => variants.find((v) => v.id === variantId) ?? variants[0],
    [variants, variantId],
  );

  const vialCount = selected?.vialCount ?? 0;
  const [picks, setPicks] = useState<string[]>([]);
  const resolvedPicks = useMemo(
    () => Array.from({ length: vialCount }, (_, i) => picks[i] ?? ""),
    [picks, vialCount],
  );

  const addItem = useCartStore((s) => s.addItem);
  const isAdding = useCartStore((s) => s.isLoading);

  if (!product || !selected) {
    return (
      <div className="mt-8">
        <p className="font-display text-2xl text-parchment">{fallbackPrice}</p>
        <p className="mt-4 text-sm text-muted-foreground">
          Not currently available — check back soon.
        </p>
      </div>
    );
  }

  const sellingPlans = getSellingPlans(selected);
  const sellingPlanId = sellingPlans[0]?.id ?? null;
  const isPreorder =
    !!sellingPlanId || (selected.availableForSale && selected.currentlyNotInStock);
  const isBuyable = selected.availableForSale || !!sellingPlanId;

  const allPicked =
    resolvedPicks.length === vialCount && resolvedPicks.every((p) => p.length > 0);

  const handlePick = (i: number, value: string) => {
    setPicks((prev) => {
      const next = Array.from({ length: vialCount }, (_, idx) => prev[idx] ?? "");
      next[i] = value;
      return next;
    });
  };

  const handleAdd = async () => {
    if (!allPicked) return;
    const attributes = resolvedPicks.map((value, i) => ({
      key: `Vial ${i + 1} fragrance`,
      value,
    }));
    await addItem({
      variantId: selected.id,
      productHandle: product.handle,
      productTitle: product.title,
      variantTitle: selected.title,
      image: product.featuredImage,
      price: selected.price,
      quantity: 1,
      maxQuantity: null,
      selectedOptions: selected.selectedOptions,
      sellingPlanId,
      attributes,
    });
    toast.success(isPreorder ? "Added to cart — pre-order" : "Added to cart", {
      description: `${product.title} — ${selected.title}`,
    });
    setPicks([]);
  };

  return (
    <div className="mt-6">
      {/* Meta row: price · pre-order eyebrow */}
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <p className="font-display text-3xl text-parchment">
          {formatMoney(selected.price.amount, selected.price.currencyCode)}
        </p>
        {isPreorder && isBuyable && (
          <>
            <span aria-hidden className="h-4 w-px bg-gold/30" />
            <p className="font-display text-[0.7rem] uppercase tracking-[0.24em] text-gold">
              Pre-order · ships when this chapter is filled
            </p>
          </>
        )}
      </div>

      {/* Number of vials — pill row */}
      {variants.length > 1 && (
        <div className="mt-7">
          <p className="eyebrow mb-3">Number of vials</p>
          <div className="grid grid-cols-3 gap-2">
            {variants.map((v) => {
              const active = selected.id === v.id;
              const vBuyable = v.availableForSale || getSellingPlans(v).length > 0;
              const label =
                v.vialCount === 1 ? "1 Vial" : `${v.vialCount} Vials`;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    setVariantId(v.id);
                    setPicks([]);
                  }}
                  disabled={!vBuyable}
                  className={`relative border px-4 py-3 font-display text-xs uppercase tracking-[0.22em] transition-all duration-300 ${
                    active
                      ? "border-gold bg-gradient-to-b from-gold/15 to-gold/5 text-gold shadow-[0_0_0_1px_color-mix(in_oklab,var(--gold)_25%,transparent)]"
                      : "border-gold/20 text-foreground/70 hover:border-gold/60 hover:text-gold"
                  } disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {label}
                  {!vBuyable && " · sold out"}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Fragrance picker card */}
      {vialCount > 0 && (
        <div className="mt-8">
          <p className="font-serif text-sm italic text-muted-foreground">
            Compose your chapter — one fragrance per vial, repeats welcome.
          </p>

          <div className="mt-3 border border-gold/15 bg-gradient-to-b from-gold/[0.03] to-transparent p-5 sm:p-6 transition-[min-height] duration-300">
            <div className="space-y-5">
              {Array.from({ length: vialCount }).map((_, i) => {
                const value = resolvedPicks[i] ?? "";
                return (
                  <div
                    key={i}
                    className="flex items-stretch gap-4 sm:gap-5"
                  >
                    <div className="flex w-8 shrink-0 flex-col items-center">
                      <span className="font-display text-sm uppercase tracking-[0.2em] text-gold">
                        {ROMAN[i]}
                      </span>
                      <span
                        aria-hidden
                        className="mt-2 w-px flex-1 bg-gold/15"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor={`vial-${i}`}
                        className="eyebrow mb-2 block"
                      >
                        Vial {i + 1} fragrance
                      </label>
                      <Select
                        value={value || undefined}
                        onValueChange={(v) => handlePick(i, v)}
                      >
                        <SelectTrigger
                          id={`vial-${i}`}
                          aria-required
                          className="h-12 w-full rounded-none border-gold/25 bg-transparent px-4 font-serif text-base text-parchment shadow-none transition-colors hover:border-gold/50 focus:border-gold focus:ring-0 focus-visible:ring-0 data-[state=open]:border-gold [&>svg]:text-gold [&>svg]:opacity-80"
                        >
                          <SelectValue
                            placeholder={
                              <span className="font-serif italic text-muted-foreground">
                                Select a fragrance…
                              </span>
                            }
                          >
                            {value && (
                              <span className="flex items-center gap-2.5">
                                <span
                                  aria-hidden
                                  className="h-1.5 w-1.5 rounded-full bg-gold"
                                />
                                <span className="font-serif text-parchment">
                                  {value}
                                </span>
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent
                          className="rounded-none border border-gold/30 bg-ink/95 backdrop-blur-md p-0 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.8)]"
                          position="popper"
                          sideOffset={6}
                        >
                          {CURRENT_CHAPTER_FRAGRANCES.map((f) => (
                            <SelectItem
                              key={f}
                              value={f}
                              className="cursor-pointer rounded-none border-l-2 border-transparent px-4 py-3 font-serif text-base text-parchment/85 focus:bg-gold/10 focus:text-gold focus:border-gold data-[state=checked]:text-gold"
                            >
                              {f}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Summary chip */}
      {allPicked && (
        <p className="mt-5 font-serif text-sm italic text-parchment/85">
          <span className="mr-2 text-gold">✦</span>
          Your chapter:{" "}
          <span className="text-parchment">
            {resolvedPicks.map(shortName).join(" · ")}
          </span>
        </p>
      )}

      {/* CTA */}
      <div className="mt-6">
        <button
          type="button"
          onClick={handleAdd}
          disabled={isAdding || !isBuyable || !allPicked}
          className={`group relative w-full overflow-hidden rounded-sm px-8 py-4 font-display text-xs uppercase tracking-[0.28em] transition-all duration-500 ${
            !isBuyable || !allPicked
              ? "border border-gold/30 bg-transparent text-gold/70 cursor-not-allowed"
              : "bg-gradient-to-r from-gold-soft to-gold text-ink hover:opacity-95 shadow-[0_10px_30px_-12px_color-mix(in_oklab,var(--gold)_45%,transparent)]"
          }`}
        >
          {isAdding ? (
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          ) : !isBuyable ? (
            "Sold out"
          ) : !allPicked ? (
            `Choose ${vialCount - resolvedPicks.filter(Boolean).length} more fragrance${
              vialCount - resolvedPicks.filter(Boolean).length === 1 ? "" : "s"
            }`
          ) : isPreorder ? (
            "Pre-order your chapter"
          ) : (
            "Add to cart"
          )}
        </button>
      </div>

      {/* Shipping aside */}
      <div className="mt-5 flex items-start gap-3 border-l border-gold/30 pl-4">
        <span aria-hidden className="mt-0.5 text-gold">✦</span>
        <p className="font-serif text-sm italic text-muted-foreground">
          Chapter Vials ship every Friday while the chapter is open. Orders placed
          after Wednesday may ship the following Friday.
        </p>
      </div>
    </div>
  );
}
